import { useCallback, useState } from 'react';
import { FormViewModalProps } from '@shared/components/ViewSelect/modals/FormViewModal';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';

export enum VIEW_SELECT_ACTION_MODE {
  NEW_MODE = 'new_mode',
  EDIT_MODE = 'edit_mode',
  DUPLICATE_MODE = 'duplicate_mode'
}

export type ViewSelectUpsertFormData = {
  name: string;
  isDefault: boolean;
};

const copyMap: Record<
  VIEW_SELECT_ACTION_MODE,
  Pick<FormViewModalProps, 'title' | 'primaryButtonText'>
> = {
  [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: {
    title: 'Edit view',
    primaryButtonText: 'Update'
  },
  [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: {
    title: 'Duplicate view',
    primaryButtonText: 'Duplicate'
  },
  [VIEW_SELECT_ACTION_MODE.NEW_MODE]: {
    title: 'Create new view',
    primaryButtonText: 'Create'
  }
};

export type ViewSelectUpsertActionFn = (
  templateName: string,
  isDefault: boolean
) => void;

const useViewSelectUpsertConfig = () => {
  const { user: userData } = useCurrentUserContext();
  const userName = userData?.username || '';
  const newViewDefaultName = `${userName}'s view`;

  const [mode, setMode] = useState<VIEW_SELECT_ACTION_MODE>(
    VIEW_SELECT_ACTION_MODE.NEW_MODE
  );
  const [modalOpened, setModalOpened] = useState(false);
  const [editId, setEditId] = useState('');

  const onEdit = useCallback((id: string) => {
    setMode(VIEW_SELECT_ACTION_MODE.EDIT_MODE);
    setEditId(id);
    setModalOpened(true);
  }, []);

  const onCreate = useCallback(() => {
    setMode(VIEW_SELECT_ACTION_MODE.NEW_MODE);
    setEditId('');
    setModalOpened(true);
  }, []);

  const onDuplicate = useCallback((id: string) => {
    setMode(VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE);
    setEditId(id);
    setModalOpened(true);
  }, []);

  const onClose = useCallback(() => setModalOpened(false), []);

  const modalCopy = copyMap[mode];

  return {
    newViewDefaultName,
    modalOpened,
    editId,
    // modalCopy can be infered
    modalCopy,
    mode,
    onEdit,
    onCreate,
    onDuplicate,
    onClose
  };
};

export default useViewSelectUpsertConfig;
